import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { GuardedDialog, DiscardConfirm, isDirty } from "@/components/support/GuardedDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { fmtDuration, fmtDate, BILLING_STATUSES, BILLING_COLOR } from "@/lib/supportApi";
import { PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const NO_TICKET = "__none__";

function Row({ label, children, testid }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-3 py-1.5">
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 sm:w-40 shrink-0">{label}</div>
      <div data-testid={testid} className="text-sm text-zinc-200 min-w-0 break-words">{children ?? <span className="text-zinc-500">—</span>}</div>
    </div>
  );
}

export function SessionDetailDialog({ open, onOpenChange, sessionId, onUpdated }) {
  const { currentOrgId } = useAuth();
  const [s, setS] = useState(null);
  const [audit, setAudit] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({});
  const [init, setInit] = useState({});
  const [confirmCancel, setConfirmCancel] = useState(false);

  const load = useCallback(() => {
    if (!sessionId || !currentOrgId) return;
    api.get(`/support/sessions/${sessionId}?org_id=${currentOrgId}`).then((r) => setS(r.data)).catch(() => {});
    api.get(`/support/sessions/${sessionId}/audit?org_id=${currentOrgId}`).then((r) => setAudit(r.data)).catch(() => setAudit([]));
  }, [sessionId, currentOrgId]);

  useEffect(() => {
    if (!open) { setEditing(false); setS(null); setAudit([]); return; }
    load();
    api.get(`/support/categories?org_id=${currentOrgId}`).then((r) => setCategories(r.data)).catch(() => {});
  }, [open, load, currentOrgId]);

  useEffect(() => {
    if (open && s?.customer_id && currentOrgId) {
      api.get(`/support/tickets?org_id=${currentOrgId}&customer_id=${s.customer_id}`).then((r) => setTickets(r.data)).catch(() => {});
    }
  }, [open, s?.customer_id, currentOrgId]);

  const startEdit = () => {
    const te = s.time_entry || {};
    const v = {
      reason: "",
      work_description: s.work_description || "",
      notes: s.review_notes || "",
      category_key: te.category_key || "",
      ticket_id: s.linked_ticket_id || NO_TICKET,
      minutes: s.time_entry ? Math.round((te.actual_seconds || 0) / 60) : "",
      billing_status: te.billing_status || "unclassified",
    };
    setF(v); setInit(v);
    setEditing(true);
  };
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.reason.trim()) { toast.error("A reason is required for every correction"); return; }
    setSaving(true);
    const body = {
      org_id: currentOrgId, reason: f.reason.trim(),
      work_description: f.work_description, notes: f.notes,
      ticket_id: f.ticket_id === NO_TICKET ? "" : f.ticket_id,
    };
    if (s.time_entry) {
      body.category_key = f.category_key || null;
      body.actual_seconds = Math.round(parseFloat(f.minutes || "0") * 60);
      body.billing_status = f.billing_status;
    }
    try {
      const r = await api.patch(`/support/sessions/${sessionId}/review-correction`, body);
      setS(r.data);
      setEditing(false);
      toast.success("Correction saved");
      load();
      onUpdated && onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save correction");
    } finally { setSaving(false); }
  };

  const dirty = editing && isDirty(f, init);
  const catLabel = (key) => categories.find((c) => c.category_key === key)?.label || key || "—";

  return (
    <GuardedDialog open={open} onOpenChange={onOpenChange} dirty={dirty} blockOutside={editing} className="bg-zinc-900 border-white/10 max-w-2xl" testid="session-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Remote session details</DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            {s ? <>{s.machine_name} · {s.customer_name || "no customer"}</> : "Loading…"}
          </DialogDescription>
        </DialogHeader>

        {!s ? (
          <div className="py-8 text-center text-sm text-zinc-500">Loading…</div>
        ) : !editing ? (
          <div className="max-h-[65vh] overflow-y-auto pr-1">
            <div className="rounded-xl border border-white/10 bg-black/30 px-4 py-2 divide-y divide-white/5">
              <Row label="Customer" testid="sd-customer">{s.customer_name}</Row>
              <Row label="Computer" testid="sd-machine">{s.machine_name}</Row>
              <Row label="Technician" testid="sd-technician">{s.technician_name}</Row>
              <Row label="Connected" testid="sd-started">{fmtDate(s.started_at)}</Row>
              <Row label="Ended" testid="sd-ended">{fmtDate(s.ended_at)}</Row>
              <Row label="Connection time" testid="sd-duration">{fmtDuration(s.duration_seconds)} {s.reconnect_count ? <span className="text-zinc-500">· {s.reconnect_count} reconnect(s)</span> : null}</Row>
              <Row label="Review status" testid="sd-status">
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${s.review_status === "reviewed" ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : s.review_status === "dismissed" ? "text-zinc-400 border-white/10" : "text-amber-400 border-amber-500/30 bg-amber-500/10"}`}>{s.review_status}</span>
                {s.corrected && <span className="ml-2 text-[10px] text-blue-300">corrected</span>}
              </Row>
              <Row label="Work description" testid="sd-work">{s.work_description}</Row>
              <Row label="Notes" testid="sd-notes">{s.review_notes}</Row>
              <Row label="Related ticket" testid="sd-ticket">{s.ticket ? <>#{s.ticket.ticket_number} {s.ticket.subject}</> : null}</Row>
              <Row label="Time entry" testid="sd-timeentry">{s.time_entry ? "linked" : "none"}</Row>
              {s.time_entry && <Row label="Actual working time" testid="sd-actual">{fmtDuration(s.time_entry.actual_seconds)}</Row>}
              {s.time_entry && <Row label="Category" testid="sd-category">{catLabel(s.time_entry.category_key)}</Row>}
              {s.time_entry && <Row label="Billing" testid="sd-billing"><span className={`text-[10px] px-1.5 py-0.5 rounded border ${BILLING_COLOR[s.time_entry.billing_status]}`}>{s.time_entry.billing_status}</span></Row>}
            </div>

            {audit.length > 0 && (
              <div className="mt-4">
                <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Correction history</div>
                <div className="rounded-xl border border-white/10 bg-black/30 divide-y divide-white/5" data-testid="sd-audit">
                  {audit.map((a) => (
                    <div key={a.audit_id} className="px-3 py-2 text-xs">
                      <div className="text-zinc-300"><span className="font-mono text-zinc-500">{a.field}</span>: <span className="text-red-300/80 line-through">{String(a.old_value ?? "—")}</span> → <span className="text-emerald-300/90">{String(a.new_value ?? "—")}</span></div>
                      <div className="text-zinc-500 mt-0.5">{fmtDate(a.created_at)} · reason: {a.reason || "—"}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="max-h-[65vh] overflow-y-auto pr-1 space-y-3">
            <div>
              <Label className="text-xs text-zinc-400">Work description</Label>
              <Textarea data-testid="sd-edit-work" value={f.work_description} onChange={(e) => set("work_description", e.target.value)} rows={2} className="mt-1 bg-black border-zinc-800" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Notes</Label>
              <Textarea data-testid="sd-edit-notes" value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="mt-1 bg-black border-zinc-800" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Related ticket</Label>
              <Select value={f.ticket_id} onValueChange={(v) => set("ticket_id", v)}>
                <SelectTrigger data-testid="sd-edit-ticket" className="mt-1 bg-black border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  <SelectItem value={NO_TICKET}>No ticket</SelectItem>
                  {tickets.map((t) => <SelectItem key={t.ticket_id} value={t.ticket_id}>#{t.ticket_number} {t.subject}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {s.time_entry ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400">Working minutes</Label>
                  <Input data-testid="sd-edit-minutes" type="number" min="0" value={f.minutes} onChange={(e) => set("minutes", e.target.value)} className="mt-1 bg-black border-zinc-800" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Category</Label>
                  <Select value={f.category_key} onValueChange={(v) => set("category_key", v)}>
                    <SelectTrigger data-testid="sd-edit-category" className="mt-1 bg-black border-zinc-800"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      {categories.map((c) => <SelectItem key={c.category_key} value={c.category_key}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Billing</Label>
                  <Select value={f.billing_status} onValueChange={(v) => set("billing_status", v)}>
                    <SelectTrigger data-testid="sd-edit-billing" className="mt-1 bg-black border-zinc-800"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      {BILLING_STATUSES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <div className="text-xs text-zinc-500">No linked time entry — working time and billing cannot be corrected here.</div>
            )}
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
              <Label className="text-xs text-amber-400">Reason for correction (required)</Label>
              <Input data-testid="sd-edit-reason" value={f.reason} onChange={(e) => set("reason", e.target.value)} placeholder="Why is this being corrected?" className="mt-1 bg-black border-zinc-800" />
              <div className="text-[11px] text-zinc-500 mt-1.5">Connection times, duration, computer and customer stay locked as historical record.</div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {!editing ? (
            <>
              {s?.can_edit && (
                <Button data-testid="sd-edit-button" variant="outline" onClick={startEdit} className="border-white/15 bg-transparent hover:bg-zinc-800">
                  <PencilSimple size={15} className="mr-1.5" /> Edit review
                </Button>
              )}
              <Button data-testid="sd-close-button" onClick={() => onOpenChange(false)} className="bg-blue-600 hover:bg-blue-500">Close</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" data-testid="sd-cancel-button" onClick={() => (dirty ? setConfirmCancel(true) : setEditing(false))} className="text-zinc-400">Cancel</Button>
              <Button data-testid="sd-save-button" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-500">{saving ? "Saving…" : "Save correction"}</Button>
            </>
          )}
        </DialogFooter>
      <DiscardConfirm open={confirmCancel} onKeep={() => setConfirmCancel(false)} onDiscard={() => { setConfirmCancel(false); setEditing(false); }} />
    </GuardedDialog>
  );
}
